import { useQuery } from "@tanstack/react-query";

import { useContainer } from "@contexts/container-context";
import { Role } from "@models/role";

import { useCurrentUser } from "./account";
import { useRole } from "./account-selectors";

/**
 * Hook to fetch a user's details (name, organization, etc.) by id.
 *
 * @param {string} userId - The id of the user
 * @param {string} [status] - Record-status filter; omitted takes the service
 *   default, which resolves active users only
 * @param {Object} [options={}] - Additional options for react-query
 * @returns {Object} The result of useQuery whose data is the user record
 */
export function useUserDetails(userId, status, options = {}) {
  const { userService } = useContainer();

  return useQuery({
    queryKey: ["userDetails", userId, status],
    queryFn: () => userService.getUserDetails(userId, { status }),
    enabled: !!userId,
    ...options,
  });
}

export function useTraineeInstructorCount(orgCode, options = {}) {
  const { userService } = useContainer();
  const role = useRole();
  const currentUser = useCurrentUser();
  const isInstructor = role === Role.INSTRUCTOR.id;
  const instructorId = isInstructor ? currentUser?.userId : null;

  return useQuery({
    queryKey: [
      "traineeInstructorCount",
      orgCode,
      isInstructor ? currentUser?.userId : null,
    ],
    queryFn: () =>
      userService.getTraineeInstructorCountForOrg(orgCode, {
        instructor_id: instructorId,
      }),
    enabled: !!orgCode && !!role,
    ...options,
  });
}
